// Client-side session state shared by the non-React API layer (http.js /
// ws.js) and the React auth components. Holds:
//   - `me`: the identity returned by /api/me ({ uid, email, role, orgId }).
//   - the superadmin's "act-as" target uid (persisted so a page reload keeps
//     you inside the same account's workspace).
//
// This is intentionally a tiny pub/sub module, not Zustand: the http/ws
// layer must read it without importing React.

const ACT_AS_KEY = 'audiosticher.actAsUid';
const ACT_AS_EMAIL_KEY = 'audiosticher.actAsEmail';
const DEVICE_KEY = 'audiosticher.deviceId';

let me = null;
const subs = new Set();
const lostSubs = new Set();

function notify() {
  for (const cb of subs) { try { cb(); } catch {} }
}

export function setMe(next) {
  me = next;
  // A non-superadmin can never act-as; clear any stale selection.
  if (!next || next.role !== 'superadmin') clearActAs();
  notify();
}

export function getMe() {
  return me;
}

export function isSuperadmin() {
  return !!me && me.role === 'superadmin';
}

export function subscribe(cb) {
  subs.add(cb);
  return () => subs.delete(cb);
}

// --- act-as (superadmin "use any account") ---

export function getActAs() {
  if (!isSuperadmin()) return '';
  try { return localStorage.getItem(ACT_AS_KEY) || ''; } catch { return ''; }
}

// Email of the account currently being acted-as (for UI labels), or ''.
export function getActAsEmail() {
  if (!isSuperadmin()) return '';
  try { return localStorage.getItem(ACT_AS_EMAIL_KEY) || ''; } catch { return ''; }
}

export function setActAs(uid, email = '') {
  try {
    if (uid) {
      localStorage.setItem(ACT_AS_KEY, uid);
      if (email) localStorage.setItem(ACT_AS_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(ACT_AS_KEY);
      localStorage.removeItem(ACT_AS_EMAIL_KEY);
    }
  } catch {}
  notify();
}

export function clearActAs() {
  setActAs('');
}

// --- device identity + single-session ---

// A stable per-browser id, shared across tabs (so multiple tabs on the same
// machine don't fight over the single session). Generated once and kept in
// localStorage. crypto.randomUUID needs a secure context (https/localhost);
// fall back to a random string on plain-http LAN access.
export function getDeviceId() {
  try {
    let d = localStorage.getItem(DEVICE_KEY);
    if (!d) {
      d = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `dev-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(DEVICE_KEY, d);
    }
    return d;
  } catch {
    return 'nodevice';
  }
}

// Fired when the server reports this device's session was superseded (the
// account was opened elsewhere). AuthGate subscribes to boot to login.
export function onSessionLost(cb) {
  lostSubs.add(cb);
  return () => lostSubs.delete(cb);
}

export function notifySessionLost() {
  for (const cb of lostSubs) { try { cb(); } catch {} }
}
